import { Metadata } from "./metadata";
import { Sdk } from "./sdk";

export interface ReferrerCode {
  code: string;
  address: string;
}

export interface ReferrerHistory {
  name: string;
  rewardAmount: string;
  description: string;
  address: string;
  conversionChainId: number;
  rewardChainId: number;
  rewardTokenAddress: string;
  rewardTokenDecimals: number;
  blockTime: string;
  txHash: string;
}

export interface ReferrerStats {
  numberOfReferrals: number;
}

export function createReferralApiPlugin(sdk: Sdk, metadata: Metadata) {
  async function getReferrerAttribution(address: string): Promise<string> {
    const url = new URL(
      `${sdk.getConfig().api}/referrer-attribution/${address}`
    );
    const meta = await metadata.getMetadata();
    if (typeof meta.referrerCode === "string") {
      url.searchParams.set("ref", meta.referrerCode);
    }
    const response = await sdk.http.send({
      url: url.toString(),
      headers: {
        "X-API-Key": sdk.getConfig().sdkKey,
      },
      method: "GET",
    });
    if (!response?.ok) {
      sdk.logger.error("request failed with http code:", response.status);
      return undefined;
    }
    const body = await response.json();
    return body.attribution;
  }

  async function getReferrerHistory(
    address: string
  ): Promise<ReferrerHistory[]> {
    const url = new URL(`${sdk.getConfig().api}/referrals/history/${address}`);

    const response = await sdk.http.send({
      url: url.toString(),
      headers: {
        "X-API-Key": sdk.getConfig().sdkKey,
      },
      method: "GET",
    });
    if (!response?.ok) {
      sdk.logger.error("request failed with http code:", response.status);
      return undefined;
    }
    const body = await response.json();
    return body.items;
  }

  async function getReferrerStats(address: string): Promise<ReferrerStats> {
    const url = new URL(`${sdk.getConfig().api}/referrals/stats/${address}`);

    const response = await sdk.http.send({
      url: url.toString(),
      headers: {
        "X-API-Key": sdk.getConfig().sdkKey,
      },
      method: "GET",
    });
    if (!response?.ok) {
      sdk.logger.error("request failed with http code:", response.status);
      return undefined;
    }
    const body = await response.json();
    return body;
  }

  async function createReferrerCode(
    address: string,
    code?: string
  ): Promise<string> {
    const response = await sdk.http.send({
      url: `${sdk.getConfig().api}/referrer-codes`,
      headers: {
        "X-API-Key": sdk.getConfig().sdkKey,
        "content-type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({ address, code }),
    });
    if (!response.ok) {
      sdk.logger.error("request failed with http code:", response.status);
      return undefined;
    }
    const body = await response.json();
    return body.code;
  }

  function validateReferrerCode(slug?: string) {
    return typeof slug === "string" && /^([a-z0-9_]){4,20}$/.test(slug);
  }

  async function checkReferrerCode(code: string): Promise<boolean> {
    const response = await sdk.http.send({
      url: `${sdk.getConfig().api}/referrer-codes/check-availability`,
      headers: {
        "X-API-Key": sdk.getConfig().sdkKey,
        "content-type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({ code }),
    });
    if (!response.ok) {
      sdk.logger.error("request failed with http code:", response.status);
      return false;
    }
    const body = await response.json();
    return body.available;
  }

  async function getReferrerCodes(
    filter: Partial<ReferrerCode> = {}
  ): Promise<ReferrerCode[]> {
    const url = new URL(`${sdk.getConfig().api}/referrer-codes`);
    if (filter.address) {
      url.searchParams.set("address", filter.address);
    }
    if (filter.code) {
      url.searchParams.set("code", filter.code);
    }
    const response = await sdk.http.send({
      url: url.toString(),
      headers: {
        "X-API-Key": sdk.getConfig().sdkKey,
      },
      method: "GET",
    });
    if (!response.ok) {
      sdk.logger.error("request failed with http code:", response.status);
      return [];
    }
    return response.json();
  }

  async function getReferrerCode(address: string): Promise<string> {
    if (!address) return;
    const referrerCodes = await getReferrerCodes({ address });
    if (referrerCodes?.length) {
      return referrerCodes[0].code;
    }
  }

  async function getReferrerAddress(code: string): Promise<string> {
    if (!code) return;
    const referrerCodes = await getReferrerCodes({ code });
    if (referrerCodes?.length) {
      return referrerCodes[0].address;
    }
  }

  return {
    getReferrerAttribution,
    getReferrerStats,
    getReferrerHistory,
    createReferrerCode,
    validateReferrerCode,
    checkReferrerCode,
    getReferrerCode,
    getReferrerAddress,
  };
}
